import { TooltipContent } from '@design-system';
import { DAYS_IN_MONTH } from '@shared/calculations/funding/projection/sharedConstants';
import { BalanceBreakdownTooltip } from '../BalanceBreakdownTooltip';
import { currency } from '../formatters';
import { HeaderTooltip } from '../HeaderTooltip';
import { indemnityPayoutWaterfall } from './indemnityPayout';
import type { StrategyPresentation } from './types';

// Generic two-pool hybrid life insurance — Allstate Whole Life, Securian
// SecureCare III/IV, and Trustmark Life + Care (`agisGroupPolicy`), plus the
// generic `hybridLifeInsurance` type. A death-benefit (base) pool is drawn first
// for LTC, then a Continuation of Benefits (COB) pool; only base drawdown
// accelerates (reduces) the death benefit. The death benefit is held STATIC by
// default (the inflation rider grows the LTC pools, not the life face) — mirrors
// CM II and the other hybrids. Modeling is unverified (no carrier illustration).
// All values are read off precomputed month state/deltas — no strategy math is
// re-implemented here.
export const genericHybridPresentation: StrategyPresentation = {
  // "Pool remaining" — two-pool open → close breakdown (death-benefit base pool
  // drawn first, then the Continuation of Benefits pool). Mirrors the sibling
  // two-pool presentation (oaAssetCarePresentation); only base drawdown also
  // accelerates the death benefit.
  poolCellTooltip: ({ month, openState }) => {
    const openBase = openState.pools.baseBenefitPool;
    const openEob = openState.pools.extensionOfBenefitsPool;
    const closeBase = month.state.pools.baseBenefitPool;
    const closeEob = month.state.pools.extensionOfBenefitsPool;
    const baseDepletion = month.deltas.base.depletion;
    const eobDepletion = month.deltas.eob.depletion;
    const baseAccrual = month.deltas.base.inflationAccrual;
    const eobAccrual = month.deltas.eob.inflationAccrual;

    const allZero =
      openBase === 0 &&
      openEob === 0 &&
      closeBase === 0 &&
      closeEob === 0 &&
      baseDepletion === 0 &&
      eobDepletion === 0 &&
      baseAccrual === 0 &&
      eobAccrual === 0;
    if (allZero) return null;

    return (
      <BalanceBreakdownTooltip
        title="LTC Pool Breakdown"
        description="Pays from the death-benefit pool first, then the Continuation of Benefits (COB) pool. Inflation accrues at policy anniversaries while each pool remains open. Only death-benefit pool drawdown reduces the death benefit; COB drawdown does not."
        sections={[
          {
            heading: 'Death-benefit pool',
            opening: openBase,
            closing: closeBase,
            lines: [
              { label: 'Inflation accrual', value: baseAccrual, kind: 'add' },
              { label: 'Drawn this month', value: baseDepletion, kind: 'subtract' },
            ],
          },
          {
            heading: 'Continuation of Benefits (COB)',
            opening: openEob,
            closing: closeEob,
            lines: [
              { label: 'Inflation accrual', value: eobAccrual, kind: 'add' },
              { label: 'Drawn this month', value: eobDepletion, kind: 'subtract' },
            ],
          },
        ]}
      />
    );
  },

  // "Projected payout" cell — reimbursement (or indemnity) waterfall from care
  // cost to policy paid to out-of-pocket. Built on universal month facts/deltas
  // (eligible cost, policy paid, the calc's per-day out-of-pocket breakdown), so
  // it stays accurate no matter how this product derives its monthly cap (a
  // death-benefit acceleration share vs a daily nominal). Unlike the Asset Care
  // sibling it omits the per-pool days × rate split — that reconciliation
  // assumes a daily-nominal cap this family doesn't necessarily use — and names
  // the funding pool(s) with a plain "drawn from" line instead.
  payoutCellTooltip: ({ month, terms }) => {
    const eligibleDays = month.facts.coverageEligibleDays;
    // A retroactive waiting-period lump (SecureCare IV, which reuses this
    // presentation) is paid on the row that SATISFIES the EP — a row with zero
    // eligible days. Bailing on eligibleDays alone would hide the breakdown for
    // the one row where real money moved. Mirrors cmTwoPresentation's guard.
    // No-op for every other product on this presentation: they never emit it.
    const epLumpSumPaid =
      (month.deltas.strategyDeltas as { epLumpSumPaid?: number } | undefined)?.epLumpSumPaid ?? 0;
    if (eligibleDays === 0 && epLumpSumPaid === 0) return null;

    const cost = month.facts.effectiveCareCost;
    const dailyCost = month.facts.dailyCareCost;
    const paid = month.deltas.payout.ltcCoveragePaid;
    const drawnBase = month.deltas.base.depletion;
    const drawnEob = month.deltas.eob.depletion;
    const isPartial = eligibleDays < DAYS_IN_MONTH;
    const eligibleCost = isPartial ? eligibleDays * dailyCost : cost;

    // OOP reasons. Their sum is total out-of-pocket, and
    // cost = paid + oopOverCap + (oopLt2Adl + oopWp + oopIl + oopPrePolicy).
    // Read the calc-supplied per-day breakdown rather than a single "ineligible
    // days" bucket so each reason is attributed honestly.
    const oopOverCap = Math.max(eligibleCost - paid, 0);
    const breakdown = month.events.ineligibleDayBreakdown;
    const oopLt2Adl = breakdown.lt2AdlDays * dailyCost;
    const oopWp = breakdown.inWpDays * dailyCost;
    const oopIl = breakdown.ilNotCoveredDays * dailyCost;
    const oopPrePolicy = breakdown.prePolicyDays * dailyCost;
    const totalOop = oopOverCap + oopLt2Adl + oopWp + oopIl + oopPrePolicy;

    const mode = terms.isIndemnity ? 'indemnity' : 'reimbursement';
    const modeText = terms.isIndemnity
      ? 'Pays a fixed cash benefit (subject to cap) from the death-benefit pool first, then the COB pool. Cash above the eligible cost banks as a surplus reserve, drained to cover cost on later shortfall months.'
      : 'Reimburses eligible incurred cost, up to the monthly cap. Funded from the death-benefit pool first, then the COB pool.';

    return (
      <TooltipContent className="max-w-xs text-xs">
        <div className="mb-1 font-semibold">Projected payout — {mode}</div>
        <div className="mb-1 text-[10px] text-gray-300">{modeText}</div>
        <div className="grid grid-cols-[auto_auto] gap-x-3 gap-y-0.5">
          {terms.isIndemnity ? (
            // Indemnity is a cash waterfall, not a day-bucket breakdown — see
            // `indemnityPayoutWaterfall`.
            indemnityPayoutWaterfall(month)
          ) : (
            <>
              <span className="text-gray-300">Care cost</span>
              <span className="font-semibold tabular-nums">{currency(cost)}</span>
              {isPartial && (
                <>
                  <span className="text-gray-300">
                    Eligible ({eligibleDays}/{DAYS_IN_MONTH} days)
                  </span>
                  <span className="font-semibold tabular-nums">{currency(eligibleCost)}</span>
                </>
              )}
              <span className="text-gray-300">Policy paid</span>
              <span className="font-semibold tabular-nums">−{currency(paid)}</span>
              <span className="text-gray-300">Out of pocket</span>
              <span className="font-semibold tabular-nums text-rose-300">{currency(totalOop)}</span>
              {oopOverCap > 0 && (
                <>
                  <span className="pl-2 text-gray-400">· over cap</span>
                  <span className="tabular-nums text-rose-300">{currency(oopOverCap)}</span>
                </>
              )}
              {oopWp > 0 && (
                <>
                  <span className="pl-2 text-gray-400">
                    · waiting period <span className="text-gray-500">· {breakdown.inWpDays}d</span>
                  </span>
                  <span className="tabular-nums text-rose-300">{currency(oopWp)}</span>
                </>
              )}
              {oopLt2Adl > 0 && (
                <>
                  <span className="pl-2 text-gray-400">
                    {'· <2 ADL'} <span className="text-gray-500">· {breakdown.lt2AdlDays}d</span>
                  </span>
                  <span className="tabular-nums text-rose-300">{currency(oopLt2Adl)}</span>
                </>
              )}
              {oopIl > 0 && (
                <>
                  <span className="pl-2 text-gray-400">
                    · independent living
                    <span className="text-gray-500"> · {breakdown.ilNotCoveredDays}d</span>
                  </span>
                  <span className="tabular-nums text-rose-300">{currency(oopIl)}</span>
                </>
              )}
              {oopPrePolicy > 0 && (
                <>
                  <span className="pl-2 text-gray-400">
                    · before policy start
                    <span className="text-gray-500"> · {breakdown.prePolicyDays}d</span>
                  </span>
                  <span className="tabular-nums text-rose-300">{currency(oopPrePolicy)}</span>
                </>
              )}
            </>
          )}
        </div>
        {drawnBase > 0 || drawnEob > 0 ? (
          <div className="mt-1 border-t border-gray-700 pt-1 text-[10px] text-gray-400">
            {drawnBase > 0 && drawnEob > 0
              ? `Drawn — death-benefit pool ${currency(drawnBase)}, COB ${currency(drawnEob)}`
              : drawnBase > 0
                ? `Drawn from death-benefit pool ${currency(drawnBase)}`
                : `Drawn from COB ${currency(drawnEob)}`}
          </div>
        ) : null}
      </TooltipContent>
    );
  },

  copy: {
    poolTooltip: (
      <HeaderTooltip
        description="End-of-month LTC pool balance (death-benefit pool + Continuation of Benefits pool), with this month's change beneath. The death-benefit pool is drawn first; the COB pool is drawn only after it is exhausted. Each pool grows on its own inflation rate while open."
        formulas={[
          { label: 'Closing pool', expression: 'opening + inflation accrual − drawn this month' },
          { label: 'Draw order', expression: 'death-benefit pool first, then COB pool' },
        ]}
      />
    ),
    payoutColumnTooltip: (
      <HeaderTooltip
        description="Policy payout this month (reimbursement or indemnity, depending on policy mode), with cumulative dollars paid by the policy through this month in parentheses. Funded from the death-benefit pool first, then the COB pool."
        formulas={[
          {
            label: 'Reimbursement mode',
            expression: 'min(eligible cost, monthly cap, pool remaining)',
          },
          { label: 'Indemnity mode', expression: 'min(monthly cap, pool remaining)' },
        ]}
        notes="Eligible cost excludes waiting-period and <2 ADL days. Indemnity pays the cap as cash regardless of actual cost; cash above the cost builds a surplus drained on later shortfall months."
      />
    ),
    deathBenefitTooltip: (
      <HeaderTooltip
        description="End-of-month death benefit, with this month's change beneath. Held at the at-issue face — it does NOT inflate. LTC draws from the death-benefit pool reduce it dollar-for-dollar; COB draws do not. Whatever remains is paid to beneficiaries."
        formulas={[
          { label: 'Death benefit', expression: 'at-issue face − Σ (death-benefit pool accelerated)' },
          { label: 'Floor', expression: 'max(above, Residual Death Benefit)' },
        ]}
        notes="The death benefit is static by default — the inflation rider grows the LTC pools, not the life face."
      />
    ),
  },
};
